import type { GeneratedInvoice } from "api/api-operations.d";

export interface TaxInvoiceQueryParams {
  page: number;
  per_page: number;
  search?: string;
}

export const buildTaxInvoiceListQuery = ({
  page = 1,
  per_page = 10,
  search,
}: Partial<TaxInvoiceQueryParams> = {}) => ({
  url: "/tax-invoices",
  method: "get" as const,
  params: { page, per_page, ...(search ? { search } : {}) },
});

export interface TaxInvoicePagination {
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

export interface TaxInvoiceListResponse {
  tax_invoices: GeneratedInvoice[];
  pagination: TaxInvoicePagination;
}

interface RawTaxInvoiceListResponse {
  data:
    | GeneratedInvoice[]
    | TaxInvoiceListResponse
    | {
        tax_invoice_details: GeneratedInvoice[];
        pagination: TaxInvoicePagination;
      };
  pagination?: TaxInvoicePagination;
}

export const normalizeTaxInvoiceList = (
  response: RawTaxInvoiceListResponse,
  { page, per_page: perPage, search }: TaxInvoiceQueryParams,
): TaxInvoiceListResponse => {
  if (!Array.isArray(response.data)) {
    if ("tax_invoice_details" in response.data) {
      return {
        tax_invoices: response.data.tax_invoice_details,
        pagination: response.data.pagination,
      };
    }
    return response.data;
  }
  if (response.pagination) {
    return { tax_invoices: response.data, pagination: response.pagination };
  }

  // Older API versions return all rows without pagination metadata.
  const query = search?.trim().toLowerCase();
  const rows = query
    ? response.data.filter((invoice) =>
        [
          invoice.sf_invoice_name,
          invoice.currency,
          invoice.gst_amount,
          invoice.invoice_amount,
          invoice.total_amount,
          invoice.tax_rate_percentage,
          invoice.tax_invoice_type,
          invoice.transaction_type,
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(query),
        ),
      )
    : response.data;
  const lastPage = Math.max(1, Math.ceil(rows.length / perPage));
  const currentPage = Math.min(page, lastPage);
  return {
    tax_invoices: rows.slice(
      (currentPage - 1) * perPage,
      currentPage * perPage,
    ),
    pagination: {
      total: rows.length,
      current_page: currentPage,
      per_page: perPage,
      last_page: lastPage,
    },
  };
};
